'use client';

import { useEffect } from 'react';
import Cookies from 'js-cookie';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { useRouter } from 'next/navigation';
import { isCapacitorApp } from '@/shared/libs/platform/is-capacitor-app';
import {
  parseAppAuthCallback,
  type ParsedAppAuthCallback,
} from '@/shared/libs/auth/register-app-auth-listener';
import { useLoginGoogle } from '@/features/auth-management/model/hooks/use-login-google';
import { useLoginTelegram } from '@/features/auth-management/model/hooks/use-login-telegram';
import { useAuthorization } from '@/entities/session/model/hooks';

const redirectAfterAuth = (nextUrl: string) => {
  window.location.assign(nextUrl || '/');
};

const AppAuthListener = () => {
  const router = useRouter();
  const { trigger: loginGoogle } = useLoginGoogle();
  const { trigger: loginTelegram } = useLoginTelegram();
  const { handleLoginSuccess, register } = useAuthorization();

  useEffect(() => {
    if (!isCapacitorApp() || typeof window === 'undefined') {
      return;
    }

    const handleParsedCallback = async (
      callback: ParsedAppAuthCallback
    ): Promise<void> => {
      await Browser.close().catch(() => undefined);

      if (callback.sessionId) {
        Cookies.set('sessionId', callback.sessionId, { expires: 7 });
        redirectAfterAuth(callback.nextUrl);
        return;
      }

      try {
        if (callback.provider === 'google' && callback.credential) {
          if (callback.authType === 'signup') {
            const response = await register({ credential: callback.credential });

            if (!response?.success) {
              router.push('/?modal=registration');
              return;
            }

            redirectAfterAuth(callback.nextUrl);
            return;
          }

          const response = await loginGoogle({ credential: callback.credential });
          await handleLoginSuccess(response?.token, response?.expired, {
            skipRedirect: true,
          });
          redirectAfterAuth(callback.nextUrl);
          return;
        }

        if (callback.provider === 'google' && callback.code) {
          const response = await loginGoogle({
            googleCode: callback.code,
            redirectURL: callback.url.origin + callback.url.pathname,
          });
          await handleLoginSuccess(response?.token, response?.expired, {
            skipRedirect: true,
          });
          redirectAfterAuth(callback.nextUrl);
          return;
        }

        if (callback.provider === 'telegram' && callback.tgQuery) {
          if (callback.authType === 'signup') {
            const response = await register({ tgQuery: callback.tgQuery });

            if (!response?.success) {
              router.push('/?modal=registration');
              return;
            }

            redirectAfterAuth(callback.nextUrl);
            return;
          }

          const response = await loginTelegram({ tgQuery: callback.tgQuery });
          await handleLoginSuccess(response?.token, response?.expired, {
            skipRedirect: true,
          });
          redirectAfterAuth(callback.nextUrl);
          return;
        }

        router.push('/?modal=auth');
      } catch (error) {
        console.error('App auth callback failed:', error);
        router.push(
          callback.authType === 'signup' ? '/?modal=registration' : '/?modal=auth'
        );
      }
    };

    let isHandling = false;
    let lastHandledUrl: string | null = null;

    const processAppUrl = async (url: string, source: 'launch' | 'event') => {
      if (!url || isHandling || lastHandledUrl === url) {
        return;
      }

      const callback = parseAppAuthCallback({ url });

      if (!callback) {
        return;
      }

      isHandling = true;
      lastHandledUrl = url;

      void handleParsedCallback(callback).finally(() => {
        isHandling = false;
      });
    };

    const registerListener = async () => {
      const launchUrl = await App.getLaunchUrl();

      if (launchUrl?.url) {
        await processAppUrl(launchUrl.url, 'launch');
      }

      const listener = await App.addListener('appUrlOpen', (event) => {
        void processAppUrl(event.url, 'event');
      });

      return listener;
    };

    let listenerPromise = registerListener();

    return () => {
      void listenerPromise.then((listener) => listener.remove());
    };
  }, [
    handleLoginSuccess,
    loginGoogle,
    loginTelegram,
    register,
    router,
  ]);

  return null;
};

export { AppAuthListener };
